import React, { useState, useEffect } from 'react';
import { Save, Cog, Plus, X } from 'lucide-react';
import '../styles/Dashboard.css';
import { useI18n } from '../utils/i18n';
import { flagInvalidField } from '../utils/formValidation';
import { useToast } from './ToastProvider';
import apiService from '../services/apiService';
import Modal from './ui/Modal';

const EmailFilterModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  currentSettings = {}, 
  loading = false 
}) => {
  const { t } = useI18n();
  const { pushToast } = useToast();
  const [filterSettings, setFilterSettings] = useState({
    hideReadEmails: false,
    hoursFilter: 0, // 0 = disabled
    enableHoursFilter: false,
    ...currentSettings
  });
  
  const [keywordsList, setKeywordsList] = useState('');
  const [additionalMailboxes, setAdditionalMailboxes] = useState([]);
  const [mailboxValidation, setMailboxValidation] = useState({}); // { [email]: true|false|'pending' }
  const [newMailboxInput, setNewMailboxInput] = useState('');

  useEffect(() => {
    if (isOpen && currentSettings) {
      setFilterSettings({
        hideReadEmails: currentSettings.hideReadEmails || false,
        hoursFilter: currentSettings.hoursFilter || 24,
        enableHoursFilter: currentSettings.enableHoursFilter || false,
        ...currentSettings
      });
      setKeywordsList(currentSettings.keywords || '');
      const list = Array.isArray(currentSettings.additionalMailboxes) ? currentSettings.additionalMailboxes : [];
      setAdditionalMailboxes(list);
      // kick off validation for existing entries
      const initState = {};
      list.forEach((m) => { initState[m] = 'pending'; });
      setMailboxValidation(initState);
      // async validate existing
      setTimeout(async () => {
        for (const m of list) {
          try {
            const res = await apiService.validateMailbox(m);
            setMailboxValidation(prev => ({ ...prev, [m]: !!res?.success }));
          } catch (_) {
            setMailboxValidation(prev => ({ ...prev, [m]: false }));
          }
        }
      }, 0);
      setNewMailboxInput('');
    }
  }, [isOpen, currentSettings]);

  const handleSave = () => {
    const settings = {
      ...filterSettings,
      keywords: keywordsList,
      additionalMailboxes: additionalMailboxes
    };
    onSave(settings);
  };

  const addMailbox = () => {
    const value = (newMailboxInput || '').trim();
    if (!value) return;
    // simple validation
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      flagInvalidField(document, '#additional-mailboxes', t('email.filter.invalidAddress', 'Bitte geben Sie eine gültige E-Mail-Adresse ein.')) || pushToast({ type: 'error', message: t('email.filter.invalidAddress', 'Bitte geben Sie eine gültige E-Mail-Adresse ein.') });
      return;
    }
    if (additionalMailboxes.includes(value)) return;
    setAdditionalMailboxes(prev => [...prev, value]);
    setMailboxValidation(prev => ({ ...prev, [value]: 'pending' }));
    // async validate via API (delegated rights)
    (async () => {
      try {
        const res = await apiService.validateMailbox(value);
        setMailboxValidation(prev => ({ ...prev, [value]: !!res?.success }));
      } catch (_) {
        setMailboxValidation(prev => ({ ...prev, [value]: false }));
      }
    })();
    setNewMailboxInput('');
  };

  const removeMailbox = (mail) => {
    setAdditionalMailboxes(prev => prev.filter(m => m !== mail));
    setMailboxValidation(prev => {
      const n = { ...prev };
      delete n[mail];
      return n;
    });
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      icon={<Cog size={24} />}
      title={t('dashboard.configureEmailSection', 'E-Mail Sektion konfigurieren')}
      closeDisabled={loading}
      closeLabel={t('common.close', 'Schließen')}
      footer={(
        <>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            disabled={loading}
          >
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="btn btn-primary"
            disabled={loading}
          >
            <Save size={16} />
            {loading ? t('common.saving', 'Speichere...') : t('common.save', 'Speichern')}
          </button>
        </>
      )}
    >
      <div className="form-body">
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filterSettings.hideReadEmails}
                onChange={(e) => setFilterSettings({
                  ...filterSettings,
                  hideReadEmails: e.target.checked
                })}
                disabled={loading}
              />
              <span className="checkmark"></span>
              {t('filters.hideReadEmails', 'Gelesene E-Mails ausblenden')}
            </label>
            <small className="form-help">
              {t('filters.hideReadEmailsHelp', 'Wenn aktiviert, werden bereits gelesene E-Mails in der Liste nicht angezeigt.')}
            </small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filterSettings.enableHoursFilter}
                onChange={(e) => setFilterSettings({
                  ...filterSettings,
                  enableHoursFilter: e.target.checked
                })}
                disabled={loading}
              />
              <span className="checkmark"></span>
              {t('filters.onlyLastHours', 'Nur E-Mails aus den letzten Stunden anzeigen')}
            </label>
            
            {filterSettings.enableHoursFilter && (
              <div className="hours-filter-container" style={{ marginTop: '10px', marginLeft: '30px' }}>
                <label htmlFor="hours-input">{t('filters.numHours', 'Anzahl Stunden:')}</label>
                <input
                  id="hours-input"
                  type="number"
                  min="1"
                  max="168" // 7 Tage
                  value={filterSettings.hoursFilter}
                  onChange={(e) => setFilterSettings({
                    ...filterSettings,
                    hoursFilter: parseInt(e.target.value) || 1
                  })}
                  className="form-input"
                  style={{ width: '80px', marginLeft: '10px' }}
                  disabled={loading}
                />
                <small className="form-help" style={{ display: 'block', marginTop: '5px' }}>
                  {t('filters.onlyLastHoursHelp', 'Zeigt nur E-Mails aus den letzten {{hours}} Stunden an.', { hours: filterSettings.hoursFilter })}
                </small>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="keywords-list">{t('filters.keywordFilter', 'Schlagwort-Filter')}</label>
            <textarea
              id="keywords-list"
              value={keywordsList}
              onChange={(e) => setKeywordsList(e.target.value)}
              placeholder={t('filters.keywordFilterPlaceholder', 'Ein Schlagwort pro Zeile eingeben...&#10;z.B.:&#10;spam@example.com&#10;URGENT&#10;Werbung')}
              rows="8"
              className="form-textarea"
              disabled={loading}
            />
            <small className="form-help">
              {t('filters.keywordEmailHelp', 'E-Mails, die diese Schlagwörter im Absender oder Betreff enthalten, werden herausgefiltert. Ein Schlagwort pro Zeile eingeben.')}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="additional-mailboxes">{t('filters.additionalMailboxes', 'Zusätzliche Postfächer')}</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                id="additional-mailboxes"
                type="email"
                value={newMailboxInput}
                onChange={(e) => setNewMailboxInput(e.target.value)}
                placeholder={t('filters.additionalMailboxPlaceholder', 'E-Mail-Adresse eingeben...')}
                className="form-input"
                disabled={loading}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={addMailbox}
                className="btn btn-secondary"
                disabled={loading}
                title={t('filters.addMailbox', 'Postfach hinzufügen')} aria-label={t('filters.addMailbox', 'Postfach hinzufügen')}
              >
                <Plus size={16} />
              </button>
            </div>
            {additionalMailboxes.length > 0 && (
              <ul style={{ marginTop: 8, paddingLeft: 18 }}>
                {additionalMailboxes.map((mail) => {
                  const status = mailboxValidation[mail];
                  const isPending = status === 'pending';
                  const isOk = status === true;
                  const isFail = status === false;
                  return (
                  <li key={mail} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isPending && <span style={{ color: 'var(--color-text-muted)' }}>⏳</span>}
                      {isOk && <span style={{ color: 'var(--color-success-alt)' }}>✔</span>}
                      {isFail && <span style={{ color: 'var(--color-danger)' }}>✖</span>}
                      <span title={isFail ? 'Kein Delegated-Zugriff (Postfach nicht verbunden)' : (isOk ? 'Delegated-Zugriff ok' : 'Prüfe Zugriff...')}>{mail}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeMailbox(mail)}
                      className="btn btn-secondary btn-sm icon-only"
                      disabled={loading}
                      title={t('filters.removeMailbox', 'Postfach entfernen')} aria-label={t('filters.removeMailbox', 'Postfach entfernen')}
                      style={{ padding: 4 }}
                    >
                      <X size={14} />
                    </button>
                  </li>
                );})}
              </ul>
            )}
            <small className="form-help">
              {t('filters.additionalMailboxesHelp', 'Fügen Sie Postfächer hinzu, auf die Sie Vollzugriff haben. E-Mails daraus werden in der Sektion mit angezeigt.')} 
            </small>
          </div>

          <div className="form-group">
            <div className="filter-preview">
              <h4>{t('filters.activeFilters', 'Aktive Filter:')}</h4>
              <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                {filterSettings.hideReadEmails && (
                  <li>{t('filters.hideReadEmailsActive', 'Gelesene E-Mails werden ausgeblendet')}</li>
                )}
                {filterSettings.enableHoursFilter && (
                  <li>{t('filters.onlyLastHoursActive', 'Nur E-Mails aus den letzten {{hours}} Stunden', { hours: filterSettings.hoursFilter })}</li>
                )}
                {keywordsList.trim() && (
                  <li>
                    {t('filters.keywordsCount', '{{count}} Schlagwort(e) werden gefiltert', { count: keywordsList.split('\n').filter(k => k.trim()).length })}
                  </li>
                )}
                {additionalMailboxes.length > 0 && (
                  <li>{t('filters.additionalMailboxesActive', '{{count}} zusätzliches/zusätzliche Postfach/Postfächer', { count: additionalMailboxes.length })}</li>
                )}
                {!filterSettings.hideReadEmails && !filterSettings.enableHoursFilter && !keywordsList.trim() && (
                  <li style={{ color: 'var(--color-text-placeholder)' }}>{t('filters.noneActive', 'Keine Filter aktiv')}</li>
                )}
              </ul>
            </div>
          </div>
        </div>
    </Modal>
  );
};

export default EmailFilterModal;